import { useMemo } from 'react';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { BorrowerApi } from '@apply/api';
import { applicationInsights } from '../../../../../../ApplicationInsights';
import { createSearchDataSource } from '../functions/createSearchDataSource';

const borrowerApi = new BorrowerApi.BorrowerApi(applicationInsights);

export interface EntityPrincipal {
    name: string;
    phone: string;
    email: string;
}

/** A borrower entity, as the entity typeahead endpoints return it. */
export interface BorrowerEntity {
    entityId: number;
    name: string;
    tin?: string;
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    /** Only included when loaded by id. */
    principals?: EntityPrincipal[];
}

const borrowerEntityQuery = (entityId: number) =>
    queryOptions({
        queryKey: ['borrowerEntity', entityId],
        queryFn: async (): Promise<BorrowerEntity> => {
            const response = await borrowerApi.entitiesTypeAheadById(entityId);
            if (response.statusCode === 200) return response.result;

            throw new Error('Failed to retrieve borrower entity.');
        },
    });

/** The selected entity, with its principals, for the details card. */
export const useBorrowerEntityQuery = (entityId: number | undefined) =>
    useQuery({ ...borrowerEntityQuery(entityId ?? 0), enabled: !!entityId });

/** Refetches an entity after it's added or edited, so the box and card show its latest details. */
export const useRefreshBorrowerEntity = () => {
    const queryClient = useQueryClient();
    return (entityId: number) => queryClient.invalidateQueries({ queryKey: borrowerEntityQuery(entityId).queryKey });
};

/** Searches borrower entities by name or TIN; the selected one comes from the same cache as the details card. */
export const useBorrowerEntitySearch = () => {
    const queryClient = useQueryClient();
    return useMemo(
        () =>
            createSearchDataSource<BorrowerEntity>({
                key: 'entityId',
                search: async (searchText, page, pageSize) => {
                    // Same request as the old EntitySelector, which didn't send a sort
                    const criteria = { searchText, take: pageSize, skip: (page - 1) * pageSize } as BorrowerApi.IEntityTypeAhead;
                    const response = await borrowerApi.entitiesTypeAhead(criteria);
                    return response.statusCode === 200 ? response.result : [];
                },
                getByKey: (entityId) => queryClient.ensureQueryData(borrowerEntityQuery(entityId)),
            }),
        [queryClient]
    );
};
